import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminApi } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'

type Dispute = {
  id: string
  order_id?: string | null
  order_number?: string | null
  customer_name?: string | null
  issue_type: string
  description?: string | null
  status: 'open' | 'under_review' | 'resolved' | 'closed' | string
  assigned_to?: string | null
  assigned_admin_name?: string | null
  created_at: string
  internal_notes?: string | null
  resolution_notes?: string | null
  resolution_type?: string | null
  refund_amount?: number | string | null
}

const ISSUE_TYPES = ['Damaged item', 'Missing item', 'Wrong service', 'Late delivery', 'Overcharged', 'Other']

export default function DisputesPage() {
  const colors = useColors()
  const { addToast } = useToast()
  const [rows, setRows] = useState<Dispute[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [sortBy, setSortBy] = useState<'created_at' | 'status' | 'issue_type'>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<any>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [adminUsers, setAdminUsers] = useState<Array<{ id: string; name: string; email?: string }>>([])
  const pageSize = 10

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true)
      setError('')
      const response = await adminApi.getDisputes({ search, status: status === 'all' ? undefined : status, page, limit: pageSize })
      const payload = response?.data ?? response
      setRows(Array.isArray(payload?.disputes) ? payload.disputes : [])
      setTotal(Number(payload?.total || 0))
    } catch {
      setError('Unable to load disputes.')
    } finally {
      setLoading(false)
    }
  }, [page, search, status])

  useEffect(() => { void fetchRows() }, [fetchRows])

  useEffect(() => {
    const loadAdmins = async () => {
      try {
        const res = await adminApi.getAdminUsers()
        const payload = res?.data ?? res
        const users = Array.isArray(payload?.users) ? payload.users : Array.isArray(payload) ? payload : []
        setAdminUsers(users)
      } catch {
        setAdminUsers([])
      }
    }
    void loadAdmins()
  }, [])

  const displayed = useMemo(() => {
    const cloned = [...rows]
    cloned.sort((a, b) => {
      const A: any = a[sortBy]
      const B: any = b[sortBy]
      if (A < B) return sortOrder === 'asc' ? -1 : 1
      if (A > B) return sortOrder === 'asc' ? 1 : -1
      return 0
    })
    return cloned
  }, [rows, sortBy, sortOrder])

  const pages = Math.max(1, Math.ceil(total / pageSize))

  const openDetail = async (id: string) => {
    setSelectedId(id)
    const res = await adminApi.getDisputeDetail(id)
    const payload = res?.data ?? res
    setDetail(payload)
  }

  const saveResolution = async () => {
    if (!selectedId || !detail) return

    // Confirm any action carrying a non-zero refund.
    const refund = detail.refund_amount ? Number(detail.refund_amount) : 0
    if (refund > 0) {
      const who = detail.customer_name || 'the customer'
      if (!window.confirm(`Issue refund of ₹${refund} to ${who}?`)) return
    }

    setActionLoading(true)
    try {
      const response = await adminApi.updateDispute(selectedId, {
        status: detail.status,
        assigned_to: detail.assigned_to || null,
        internal_notes: detail.internal_notes || null,
        resolution_notes: detail.resolution_notes || null,
        resolution_type: detail.resolution_type || null,
        refund_amount: detail.refund_amount ? Number(detail.refund_amount) : null,
      })
      const serverMessage = (response as { message?: string })?.message
      if (serverMessage) {
        const looksLikeWarning = /skip|den|cannot|unable|not\b/i.test(serverMessage)
        addToast(serverMessage, looksLikeWarning ? 'info' : 'success')
      } else {
        addToast('Dispute updated', 'success')
      }
      await fetchRows()
      await openDetail(selectedId)
    } finally {
      setActionLoading(false)
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>Disputes</h2>
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search by order/customer" style={{ flex: 1 }} />
          <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1) }}>
            <option value="all">All</option><option value="open">Open</option><option value="under_review">Under Review</option><option value="resolved">Resolved</option><option value="closed">Closed</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}><option value="created_at">Created</option><option value="status">Status</option><option value="issue_type">Issue Type</option></select>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as any)}><option value="desc">Desc</option><option value="asc">Asc</option></select>
        </div>
        {loading ? <div style={{ padding: 20, color: colors.textMuted }}>Loading disputes...</div> : error ? <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div> : displayed.length === 0 ? <div style={{ padding: 20, color: colors.textMuted }}>No disputes found.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th>Order #</th><th>Customer</th><th>Issue Type</th><th>Description</th><th>Status</th><th>Assigned To</th><th>Created</th></tr></thead>
            <tbody>
              {displayed.map((r) => (
                <tr key={r.id} onClick={() => void openDetail(r.id)} style={{ cursor: 'pointer', background: selectedId === r.id ? 'rgba(201,169,110,0.12)' : 'transparent' }}>
                  <td>{r.order_number || '-'}</td><td>{r.customer_name || '-'}</td><td>{r.issue_type}</td><td>{r.description || '-'}</td><td>{r.status}</td><td>{r.assigned_admin_name || '-'}</td><td>{new Date(r.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      {detail ? (
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
          <h3 style={{ marginTop: 0 }}>Dispute Detail</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 10 }}>
            <div>Order: {detail.order_number || '-'}</div>
            <div>Customer: {detail.customer_name || '-'} ({detail.customer_phone || '-'})</div>
            <div>Issue: {detail.issue_type}</div>
            <div>Created: {new Date(detail.created_at).toLocaleString()}</div>
          </div>
          <div style={{ marginTop: 8 }}><strong>Description:</strong> {detail.description || '-'}</div>
          <div style={{ marginTop: 8 }}><strong>Photos:</strong> {Array.isArray(detail.photos) && detail.photos.length ? detail.photos.map((p: string) => <a key={p} href={p} target="_blank" rel="noreferrer" style={{ marginRight: 8 }}>{p.slice(0, 30)}...</a>) : 'None'}</div>
          <div style={{ marginTop: 8 }}>
            <strong>Timeline:</strong>
            <ul>
              {(detail.timeline || []).map((m: any) => <li key={m.id}>{new Date(m.created_at).toLocaleString()} - {m.sender_name || m.sender_role}: {m.message}</li>)}
            </ul>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
            <select value={detail.status || 'open'} onChange={(e) => setDetail((d: any) => ({ ...d, status: e.target.value }))}><option value="open">Open</option><option value="under_review">Under Review</option><option value="resolved">Resolved</option><option value="closed">Closed</option></select>
            <select value={detail.assigned_to || ''} onChange={(e) => setDetail((d: any) => ({ ...d, assigned_to: e.target.value || null }))}>
              <option value="">Unassigned</option>
              {adminUsers.map((u) => <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>)}
            </select>
            <select value={detail.resolution_type || ''} onChange={(e) => setDetail((d: any) => ({ ...d, resolution_type: e.target.value }))}>
              <option value="">Resolution Action</option><option value="full_refund">Full refund</option><option value="partial_refund">Partial refund</option><option value="re_service">Re-service</option><option value="apology_credit">Apology credit</option>
            </select>
          </div>
          <textarea value={detail.resolution_notes || ''} onChange={(e) => setDetail((d: any) => ({ ...d, resolution_notes: e.target.value }))} placeholder="Resolution notes" rows={2} style={{ width: '100%', marginTop: 8 }} />
          <textarea value={detail.internal_notes || ''} onChange={(e) => setDetail((d: any) => ({ ...d, internal_notes: e.target.value }))} placeholder="Internal notes" rows={2} style={{ width: '100%', marginTop: 8 }} />
          <input type="number" min="0" step="0.01" value={detail.refund_amount || ''} onChange={(e) => setDetail((d: any) => ({ ...d, refund_amount: e.target.value }))} placeholder="Refund amount" style={{ marginTop: 8 }} />
          <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
            <button onClick={() => void saveResolution()} disabled={actionLoading}>{actionLoading ? 'Saving...' : 'Save changes'}</button>
            <button onClick={async () => { const message = prompt('Add timeline message') || ''; if (message) { await adminApi.addDisputeMessage(detail.id, message); await openDetail(detail.id) } }}>Add message</button>
            <a href={detail.order_id ? `/orders?search=${detail.order_number || detail.order_id}` : '#'}>Open order details</a>
          </div>
        </div>
      ) : null}

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>Create Dispute</h3>
        <CreateDisputeForm onCreated={() => void fetchRows()} />
      </div>
    </div>
  )
}

function CreateDisputeForm({ onCreated }: { onCreated: () => void }) {
  const [form, setForm] = useState({ order_id: '', customer_id: '', issue_type: ISSUE_TYPES[0], description: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const submit = async () => {
    if (!form.issue_type) return
    setLoading(true)
    setError('')
    try {
      await adminApi.createDispute({ order_id: form.order_id || undefined, customer_id: form.customer_id || undefined, issue_type: form.issue_type, description: form.description || undefined })
      setForm({ order_id: '', customer_id: '', issue_type: ISSUE_TYPES[0], description: '' })
      onCreated()
    } catch {
      setError('Unable to create dispute.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      {error ? <div style={{ color: '#b91c1c', marginBottom: 8 }}>{error}</div> : null}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 8 }}>
        <input value={form.order_id} onChange={(e) => setForm((f) => ({ ...f, order_id: e.target.value }))} placeholder="Order ID" />
        <input value={form.customer_id} onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value }))} placeholder="Customer ID" />
        <select value={form.issue_type} onChange={(e) => setForm((f) => ({ ...f, issue_type: e.target.value }))}>{ISSUE_TYPES.map((i) => <option key={i} value={i}>{i}</option>)}</select>
        <button onClick={() => void submit()} disabled={loading}>{loading ? 'Creating...' : 'Create'}</button>
      </div>
      <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Description" style={{ width: '100%', marginTop: 8 }} rows={2} />
    </div>
  )
}
